import React from 'react';
import styled from 'styled-components';

import BoltIconPurple from '../../assets/svg/bolt-purple.svg';
import IdentifierValue from '../IdentifierValue';
import { getQuickLauncherDisplayPrefix, getQuickLauncherPreviewText, QuickLauncherDestination } from './quick-launcher.util';

export const ResultButton = styled.button`
  align-items: center;
  background: ${(props) => props.theme.colors.bgAccent};
  border: 1px solid rgba(58, 49, 94, 0.08);
  border-radius: 6px;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  outline: none;
  padding: 12px 14px;
  text-align: left;
  transition: background 0.18s, border-color 0.18s, transform 0.18s;
  width: 100%;

  &:focus,
  &:hover {
    background: ${(props) => props.theme.colors.surface};
    border-color: rgba(58, 49, 94, 0.26);
    transform: translateY(-1px);
  }
`;

export const ResultList = styled.div`
  display: grid;
  gap: 8px;
`;

export const ResultMain = styled.span`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

export const Badge = styled.span`
  align-items: center;
  background: ${(props) => props.theme.colors.primaryBrand};
  border-radius: 4px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 auto;
  font-family: ${(props) => props.theme.typography.face.primary};
  font-size: 0.75em;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 0;
  margin-right: 10px;
  padding: 5px 8px;
`;

const EvidenceWorkspaceFolderIcon = () => (
  <svg aria-hidden="true" fill="none" height="14" viewBox="0 0 24 24" width="14" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

export const ResultText = styled.span`
  color: ${(props) => props.theme.colors.body};
  display: block;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 0.9375em;
  font-weight: 600;
  letter-spacing: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ResultMeta = styled(IdentifierValue)`
  color: ${(props) => props.theme.colors.placeholder};
  display: block;
  flex: 0 1 46%;
  font-size: 0.8125em;
  letter-spacing: 0;
  margin-left: 10px;
  min-width: 0;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const HelpCard = styled.div`
  background: #f4f1fa;
  border: 1px solid rgba(58, 49, 94, 0.12);
  border-radius: 6px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.74);
  box-sizing: border-box;
  display: grid;
  gap: 10px;
  padding: 14px;
`;

export const HelpHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

export const HelpIcon = styled.span`
  background: #fff url(${BoltIconPurple}) center center no-repeat;
  background-size: 15px;
  border: 1px solid rgba(58, 49, 94, 0.1);
  border-radius: 6px;
  box-shadow: 0 2px 5px rgba(25, 21, 43, 0.08);
  flex: 0 0 auto;
  height: 30px;
  width: 30px;
`;

export const HelpContent = styled.div`
  min-width: 0;
`;

export const HelpTitle = styled.div`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.primary};
  font-size: 0.9375em;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  margin-bottom: 5px;
`;

export const HelpText = styled.div`
  color: ${(props) => props.theme.colors.body2};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 0.875em;
  letter-spacing: 0;
  line-height: 1.4;
`;

export const HelpFooter = styled.div`
  align-items: center;
  border-top: 1px solid rgba(58, 49, 94, 0.1);
  color: ${(props) => props.theme.colors.placeholder};
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 0.8125em;
  gap: 4px;
  justify-content: center;
  letter-spacing: 0;
  line-height: 1.4;
  padding-top: 10px;
  text-align: center;
`;

export const ShortcutSeparator = styled.span`
  color: inherit;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 1em;
  letter-spacing: 0;
  line-height: 1;
`;

export const KeyCap = styled.span`
  background: ${(props) => props.theme.colors.surface};
  border: 1px solid rgba(58, 49, 94, 0.18);
  border-bottom-color: rgba(58, 49, 94, 0.34);
  border-radius: 4px;
  box-shadow: 0 1px 0 rgba(58, 49, 94, 0.16);
  color: ${(props) => props.theme.colors.body};
  display: inline-block;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.82em;
  letter-spacing: 0;
  line-height: 1;
  margin: 0 2px;
  padding: 3px 6px;
  vertical-align: 1px;
`;

interface QuickLauncherResultButtonProps {
  className?: string;
  destination: QuickLauncherDestination;
  onClick: () => void;
}

export const QuickLauncherResultButton: React.FC<QuickLauncherResultButtonProps> = ({ className, destination, onClick }) => (
  <ResultButton className={className} type="button" onClick={onClick}>
    <ResultMain>
      <Badge>
        {destination.kind === 'evidence-workspace' ? <EvidenceWorkspaceFolderIcon /> : getQuickLauncherDisplayPrefix(destination)}
      </Badge>
      <ResultText>{getQuickLauncherPreviewText(destination)}</ResultText>
    </ResultMain>
    {destination.kind === 'search' ? null : <ResultMeta title={destination.id}>{destination.id}</ResultMeta>}
  </ResultButton>
);

interface QuickLauncherHelpCardProps {
  className?: string;
  footer: React.ReactNode;
}

export const QuickLauncherHelpCard: React.FC<QuickLauncherHelpCardProps> = ({ className, footer }) => (
  <HelpCard className={className}>
    <HelpHeader>
      <HelpIcon />
      <HelpContent>
        <HelpTitle>Quick jump</HelpTitle>
        <HelpText>
          Paste an ID, or an email, then press <KeyCap>Enter</KeyCap>. An email lists reposits, users, claims, and organisations.
        </HelpText>
      </HelpContent>
    </HelpHeader>
    <HelpFooter>{footer}</HelpFooter>
  </HelpCard>
);
